import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useStore } from 'react-redux';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import Modal from '../Modal';
import CaseProgress from './CaseProgress';
import CaseActivity from './CaseActivity';
import CaseFiles from './CaseFiles';
import CaseNotes from './CaseNotes';
import CaseExport from './CaseExport';
import CaseNote from './CaseNote';
import AlertModal from '../AlertModal';

import {
  fetchUsers,
  fetchHospitals,
  fetchDistributors,
  fetchDocuments,
} from '../../../actions/adminActions';
import { fetchSurgeons } from '../../../actions/surgeonsActions';
import { fetchProcedures } from '../../../actions/proceduresActions';
import {
  setActiveCase,
  setActiveTab,
  deleteCase,
  fetchNotes,
  notifyUsers,
  simpleUpdateCase,
  restoreCase,
  postponeCase,
  fetchCase,
} from '../../../actions/casesActions';

import { roleNames } from '../../../constants/userRoles';
import { caseConstants, urls } from '../../../constants';

import { withFirebase } from '../../../firebase';
import { getFile } from '../../../util/api';

import './case-modal.scss';

const { procedureFlows, statuses } = caseConstants;

const CaseModal = (props) => {
  const {
    open,
    fetchHospitals,
    fetchProcedures,
    fetchSurgeons,
    fetchDistributors,
    fetchDocuments,
    fetchUsers,
    fetchNotes,
    activeCase,
    activeTab,
    setActiveTab,
    setActiveCase,
    firebase,
    currentUser,
    deleteCase,
    restoreCase,
    trainingMode,
    selectedFlow,
    notifyUsers,
    simpleUpdateCase,
    onRequestDateClick,
    onTBCClick,
    onAdminApproveDateClick,
    onRequestConsultationDateClick,
    onAdminApproveConsultationDateClick,
    postponeCase,
    fetchCase,
    procedures = [],
    documents = [],
  } = props;
  const store = useStore();

  const [loading, setLoading] = useState(false);
  const [isOpen, toggleModal] = useState(false);

  const [tabModalOpen, toggleTabModal] = useState(false);
  const [nextTab, setNextTab] = useState(undefined);

  const [deleteModalOpen, toggleDeleteModal] = useState(false);
  const [restoreModalOpen, toggleRestoreModal] = useState(false);
  const [postponeModalOpen, togglePostponeModal] = useState(false);
  const [exportState, toggleExportState] = useState(false);
  const [noteState, toggleNoteState] = useState(false);
  const [exporting, toggleExporting] = useState(false);

  const [undoModalOpen, toggleUndoModal] = useState(false);

  const onClose = (isFirstStep, caseId, options = {}) => {
    toggleExportState(false);
    setActiveTab(0);
    const latestActiveCase = options.updatedCase || store.getState()?.cases?.activeCase || activeCase;
    props.onClose(isFirstStep, selectedFlow, { caseId, updatedCase: latestActiveCase });
  };

  useEffect(() => {
    if (open && activeCase?.id) {
      onLoad(activeCase.id).catch();
    }
  }, [open, activeCase?.id]); // eslint-disable-line

  const onLoad = async (activeCaseId) => {
    try {
      setLoading(true);

      fetchNotes(activeCaseId).catch((err) => console.log(err));

      const res = await fetchCase(activeCaseId);

      const files = res && res.files ? res.files.slice() : [];
      const downloadLinks = [];

      files.forEach((file) => {
        downloadLinks.push(firebase.getDownloadLink(`${res.id}/${file.id}`));
      });

      const promises = [
        fetchUsers(),
        fetchProcedures(),
        fetchDocuments(),
        fetchHospitals(),
        fetchSurgeons(),
        fetchDistributors(),
      ];

      Promise.all(promises).finally(() => setLoading(false));
      Promise.allSettled(downloadLinks)
        .then((links) => {
          files.forEach((file, index) => {
            const result = links[index];
            file.downloadLink = result?.status === "fulfilled" ? result.value : null;
          });

          const latestActiveCase = store.getState()?.cases?.activeCase;

          if (latestActiveCase?.id !== res.id) {
            return;
          }

          const hasCaseMovedOn = latestActiveCase?.step !== res.step || latestActiveCase?.status !== res.status;

          if (hasCaseMovedOn) {
            return;
          }

          setActiveCase({ ...latestActiveCase, files });
        })
        .catch((err) => console.log(err));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   if (activeTab === 2) {
  //     fetchFiles().catch();
  //   }
  // }, [activeTab]);

  const handleClose = (isFirstStep) => {
    if (activeCase && (activeCase.step === 1 || activeCase.step === 9) && activeTab === 0) {
      toggleModal(true);
    } else {
      onClose(isFirstStep);
    }
  };

  const closeCaseModal = () => {
    toggleModal(false);
    onClose();
  };

  const onDeleteCase = async () => {
    toggleDeleteModal(false);
    const updatedCase = { ...activeCase, archived: true };
    await deleteCase(activeCase.id);
    onClose(undefined, undefined, { updatedCase });
  };

  const onRestoreCase = async () => {
    toggleRestoreModal(false);
    const updatedCase = { ...activeCase, archived: false };
    await restoreCase(activeCase.id);
    onClose(undefined, undefined, { updatedCase });
  };

  const onExport = async (address, phoneNumber) => {
    toggleExporting(true);

    try {
      await getFile(urls.exportCase, { caseId, address, phoneNumber }, caseId);
      toggleExportState(false);
    } catch (err) {
      console.error(err);
    } finally {
      toggleExporting(false);
    }
  };

  const onRevert = async () => {
    try {
      if (activeCase?.step > 0) {
        const doc = {
          ...activeCase,
          status: statuses.inProgress,
          step: activeCase?.step - 1
        }
        if ([procedureFlows.HTO, procedureFlows.HTO_LATERAL, procedureFlows.DFO_Lateral, procedureFlows.DFO_Medial].includes(activeCase?.procedureFlow) && activeCase.step === 3) {
          doc.alignmentData = {};
        }
        await simpleUpdateCase(doc);
        toggleUndoModal(false);
        handleClose();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onPostponeCase = async () => {
    try {
      await postponeCase(activeCase);
      togglePostponeModal(false);
      handleClose();
    } catch (err) {
      console.error(err);
    }
  };

  const renderContent = () => {
    if (exportState) {
      return (
        <CaseExport
          onClose={() => toggleExportState(false)}
          onSubmit={onExport}
          loading={exporting}
        />
      );
    }

    if (noteState) {
      return (
        <CaseNote
          onClose={() => toggleNoteState(false)}
          caseNote={activeCase?.caseNote}
        />
      );
    }

    switch (activeTab) {
      case 0:
        return <CaseProgress onClose={onClose} toggleModal={toggleModal} />;
      case 1:
        return <CaseActivity onClose={onClose} />;
      case 2:
        return <CaseFiles onClose={onClose} />;
      case 3:
        return <CaseNotes onClose={onClose} />;
      default:
        return <CaseProgress />;
    }
  };

  const tabs = useMemo(() => {
    const result = [
      { label: 'Process' },
      { label: 'Activity', disabled: !activeCase || activeCase.step === 0 },
      { label: 'Files', disabled: !activeCase || activeCase.step === 0 },
      { label: 'Notes', disabled: !activeCase || activeCase.step === 0 },
    ];

    // if (currentUser.role !== roleNames.SURGEON && currentUser.role !== roleNames.PRACTICE_MANAGER) {
    //   result?.push({ label: 'Notes' });
    // }

    return result;
  }, [activeCase]);

  const isAdmin = currentUser.role === roleNames.GLOBAL_ADMIN || currentUser.role === roleNames.COUNTRY_ADMIN;
  const isGlobalAdmin = currentUser.role === roleNames.GLOBAL_ADMIN;
  const isCountryAdmin = currentUser.role === roleNames.COUNTRY_ADMIN;
  const isDistributorAdmin = currentUser.role === roleNames.DISTRIBUTOR_ADMIN;
  const isSeniorEngineer = currentUser.role === roleNames.ENGINEER && currentUser.engineerLevel === 1;
  const isRadiology = currentUser.role === roleNames.RADIOLOGY;

  const caseId = activeCase && activeCase.id;
  const isExistingCaseHydrating = !!caseId && !activeCase?.isHydrated;
  const noTabs = (activeCase && (!activeCase.step || exportState)) || !activeCase || isRadiology;

  const exportMenuItems = useMemo(() => {
    if (!activeCase?.procedure) {
      return [];
    }

    const procedure = procedures.find((item) => item.id === activeCase.procedure);
    if (!procedure?.documents?.length) {
      return [];
    }

    return procedure.documents
      .map((documentItem) => {
        if (typeof documentItem === 'string') {
          const linkedDocument = documents.find((doc) => doc.id === documentItem);
          return linkedDocument ? { id: linkedDocument.id, label: linkedDocument.name, url: linkedDocument.url } : null;
        }

        if (documentItem?.id && documentItem?.url) {
          return { id: documentItem.id, label: documentItem.name, url: documentItem.url };
        }

        if (documentItem?.id) {
          const linkedDocument = documents.find((doc) => doc.id === documentItem.id);
          return linkedDocument ? { id: linkedDocument.id, label: linkedDocument.name, url: linkedDocument.url } : null;
        }

        return null;
      })
      .filter((item) => !!item?.url);
  }, [activeCase, procedures, documents]);

  const isTrainingModeTurnedOn = () => {
    if (activeCase) {
      return (
        (!activeCase.step && trainingMode) || (activeCase.step !== 0 && activeCase.trainingMode)
      );
    }
    return false;
  };

  const onTabChange = (value) => {
    if (activeCase.step === 1 && activeTab === 0) {
      setNextTab(value);
      toggleTabModal(true);
    } else {
      setActiveTab(value);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      onTabChange={onTabChange}
      title={caseId ? (activeCase?.formattedId || activeCase?.id) : 'New Case'}
      subtitle={caseId && 'Case ID'}
      caseId={caseId}
      tabs={!noTabs ? tabs : null}
      activeTab={activeTab}
      size="lg"
      isCase
      isAdmin={isAdmin}
      isDistributorAdmin={isDistributorAdmin}
      isGlobalAdmin={isGlobalAdmin}
      isSeniorEngineer={isSeniorEngineer}
      onDeleteClick={() => toggleDeleteModal(true)}
      onRestoreClick={() => toggleRestoreModal(true)}
      onExportClick={() => toggleExportState(true)}
      onPostponeClick={() => togglePostponeModal(true)}
      step={activeCase ? activeCase.step : null}
      trainingMode={isTrainingModeTurnedOn()}
      flow={activeCase?.procedureFlow ? activeCase?.procedureFlow : selectedFlow}
      withExport
      exportMenuItems={exportMenuItems}
      isPaid={activeCase?.procedureFlow === procedureFlows.ACL && !!activeCase.isPaid}
      caseNote={activeCase?.caseNote}
      onFilesClick={() => setActiveTab(2)}
      onCaseNoteClick={() => setActiveTab(3)}
      onNotifyUsersClick={() => notifyUsers(activeCase)}
      onUndoClick={() => toggleUndoModal(true)}
      onAdminApproveDateClick={onAdminApproveDateClick}
      onRequestConsultationDateClick={onRequestConsultationDateClick}
      onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
      onTBCClick={onTBCClick}
      onRequestDateClick={onRequestDateClick}
      canRequestDateChange={!isAdmin || isCountryAdmin}
      canApproveConsultationDateChange={isGlobalAdmin || isCountryAdmin || isDistributorAdmin}
      canRequestConsultationDateChange={!isGlobalAdmin && !isCountryAdmin && !isDistributorAdmin}
      activeCase={activeCase}
    >
      {loading || isExistingCaseHydrating ? (
        <Box className="loading-container" sx={{ minWidth: { md: '700px' } }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className={`case-modal-container ${noTabs && 'no-tabs'}`}>{renderContent()}</div>
      )}

      {activeCase && (
        <AlertModal
          open={isOpen}
          onClose={() => toggleModal(false)}
          onSubmit={closeCaseModal}
          title="Are you sure?"
          text="Closing this step will leave existing case files untouched"
          submitText="Close"
        />
      )}
      {activeCase && (
        <AlertModal
          open={tabModalOpen}
          onClose={() => toggleTabModal(false)}
          onSubmit={() => {
            setActiveTab(nextTab);
            toggleTabModal(false);
            setNextTab(undefined);
          }}
          title="Are you sure?"
          text="Changing the tab will leave existing case files untouched"
          submitText="Submit"
        />
      )}
      {(isAdmin || isSeniorEngineer) && (
        <AlertModal
          open={deleteModalOpen}
          onClose={() => toggleDeleteModal(false)}
          onSubmit={onDeleteCase}
          title="Are you sure?"
          text="Deleting this case will also delete all related files"
          submitText="Delete"
        />
      )}
      {isAdmin && (
        <AlertModal
          open={restoreModalOpen}
          onClose={() => toggleRestoreModal(false)}
          onSubmit={onRestoreCase}
          title="Restore Case"
          text="Are you sure you want to restore this case back to an active status?"
          submitText="Restore"
          closeText="Cancel"
        />
      )}
      {(isAdmin || isSeniorEngineer) && (
        <AlertModal
          open={undoModalOpen}
          onClose={() => toggleUndoModal(false)}
          onSubmit={onRevert}
          title="Go Back to Previous Step?"
          text="Are you sure you want revert this case back to the previous step, as all data and files for that step will be lost!"
          closeText="Cancel"
          submitText="Revert"
        />
      )}
      {(isAdmin || isDistributorAdmin) && (
        <AlertModal
          open={postponeModalOpen}
          onClose={() => togglePostponeModal(false)}
          onSubmit={onPostponeCase}
          title="Postpone Case?"
          text="Are you sure you want to postpone this case. The case date will be cleared and associated users will be notified?"
          closeText="Cancel"
          submitText="Postpone"
        />
      )}
    </Modal>
  );
};

CaseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  currentUser: PropTypes.object,
  activeCase: PropTypes.object,
  fetchUsers: PropTypes.func,
  fetchHospitals: PropTypes.func,
  fetchProcedures: PropTypes.func,
  fetchDocuments: PropTypes.func,
  fetchSurgeons: PropTypes.func,
  setActiveTab: PropTypes.func,
  setActiveCase: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.user.currentUser,
    activeCase: state.cases.activeCase,
    activeTab: state.cases.activeTab,
    selectedFlow: state.cases.selectedFlow,
    trainingMode: state.sidebar.trainingMode,
    procedures: state.procedures.list,
    documents: state.documents.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchUsers: () => dispatch(fetchUsers()),
    fetchHospitals: () => dispatch(fetchHospitals()),
    fetchProcedures: () => dispatch(fetchProcedures()),
    fetchDocuments: () => dispatch(fetchDocuments()),
    fetchSurgeons: () => dispatch(fetchSurgeons()),
    fetchDistributors: () => dispatch(fetchDistributors()),
    fetchNotes: (caseId) => dispatch(fetchNotes(caseId)),
    setActiveTab: (tab) => dispatch(setActiveTab(tab)),
    setActiveCase: (activeCase) => dispatch(setActiveCase(activeCase)),
    deleteCase: (caseId) => dispatch(deleteCase(caseId)),
    restoreCase: (caseId) => dispatch(restoreCase(caseId)),
    notifyUsers: (activeCase) => dispatch(notifyUsers(activeCase)),
    simpleUpdateCase: (data) => dispatch(simpleUpdateCase(data)),
    postponeCase: (data) => dispatch(postponeCase(data)),
    // fetchCaseById: (caseId) => dispatch(fetchCaseById(caseId)),
    fetchCase: (caseId) => dispatch(fetchCase(caseId))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withFirebase(CaseModal));
